import { Injectable, PendingTasks, inject, signal, type Provider } from '@angular/core';
import { NGX_ECHARTS_CONFIG } from 'ngx-echarts';
import type { echarts } from './echarts.providers';

type Echarts = typeof echarts;

/**
 * Chargement unique du chunk ECharts (`echarts.providers.ts`, hors du bundle initial), partagé par
 * tous les graphes : la directive `echarts` de ngx-echarts (cf. `provideEcharts()`), la carte de
 * Segments, et le signal `pret` qui garde le montage de cette directive.
 *
 * Pourquoi : la directive crée son graphe dans un `setTimeout`, puis attend le module. Détruite
 * pendant cette attente, elle n'annule rien : le graphe naît quand même, sur un `div` déjà retiré
 * de la page (taille 0, jamais libéré), et ses sorties émettent après la destruction (NG0953). La
 * carte y levait en plus un `TypeError` (projection d'une zone de 0 px). Un `import()`, même d'un
 * module déjà chargé, rend la main au navigateur, donc à une détection de changements qui peut
 * détruire la directive : une fois le module là, `charger()` le rend par `Promise.resolve()`, et le
 * graphe naît dans la tâche du `setTimeout`, que la destruction annule. Reste la première attente,
 * celle du téléchargement : `pret` passe à vrai à son arrivée, et la directive n'est montée qu'à
 * partir de là (`trend-chart`, `health-score`, carte de Segments via `charger()`).
 */
@Injectable({ providedIn: 'root' })
export class EchartsLoaderService {
  // Suivi par Angular : `whenStable()` attend le module, en test comme pour l'hydratation.
  private readonly pendingTasks = inject(PendingTasks);
  private module: Echarts | null = null;
  private enCours: Promise<Echarts> | null = null;

  private readonly _pret = signal(false);
  /** Vrai une fois le module chargé : la directive `echarts` peut alors être montée. */
  readonly pret = this._pret.asReadonly();

  /** Lance le chargement au premier appel ; une fois le module là, le rend sans rendre la main. */
  charger(): Promise<Echarts> {
    if (this.module) {
      return Promise.resolve(this.module);
    }
    if (!this.enCours) {
      const fin = this.pendingTasks.add();
      this.enCours = import('./echarts.providers')
        .then((m) => {
          this.module = m.echarts;
          // Avant `fin()` : la détection de changements qui monte les graphes est planifiée avant
          // que `whenStable()` ne voie la tâche se terminer.
          this._pret.set(true);
          return m.echarts;
        })
        .catch((e: unknown) => {
          // Chunk introuvable (déploiement entre-temps, réseau) : le graphe suivant réessaie.
          this.enCours = null;
          throw e;
        })
        .finally(fin);
    }
    return this.enCours;
  }
}

/** Config de ngx-echarts : le module passe par `EchartsLoaderService`, jamais par un `import()` direct. */
export function provideEcharts(): Provider {
  return {
    provide: NGX_ECHARTS_CONFIG,
    useFactory: () => {
      const loader = inject(EchartsLoaderService);
      return { echarts: () => loader.charger() };
    },
  };
}
